import { supabase } from './supabase';
import { storageService } from './storage';
import { Beat, Verse, ContentFilters, VerseStatus } from '../types/content';

export interface CreateBeatData {
  title: string;
  genre: string;
  bpm: number;
  tags: string[];
  file_url: string;
  is_public: boolean;
  verses_count?: number;
  beat_type?: 'cypher' | 'collaboration';
}

export interface CreateVerseData {
  beat_id: string;
  file_url: string;
}

/** Fired after a producer reviews a verse so pending counters can refresh. */
export const VERSE_REVIEWED_EVENT = 'dhh:verse-reviewed';

/**
 * Who may see a non-approved take: the rapper who recorded it, and the
 * producer who owns the beat. Approved verses (and pre-migration rows with
 * no status) are public. Applied after every fetch so a missing RLS policy
 * cannot turn the beat-details list into a leak.
 */
function isVerseVisibleToViewer(
  verse: { status?: string | null; rapper_id?: string; beat?: { producer_id?: string } | null },
  viewerId?: string | null
): boolean {
  const status = verse.status || 'approved';
  if (status === 'approved') return true;
  if (!viewerId) return false;
  if (verse.rapper_id === viewerId) return true;
  if (verse.beat?.producer_id === viewerId) return true;
  return false;
}

export const contentService = {
  async createBeat(data: CreateBeatData): Promise<Beat> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Fetch the producer's state from the profiles table
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('state')
      .eq('id', user.id)
      .single();
    if (profileError || !profile) {
      throw new Error('Could not fetch user profile for state');
    }

    // Ensure tags is always an array of strings
    let tagsArray: string[] = [];
    if (Array.isArray(data.tags)) {
      tagsArray = data.tags;
    } else if (typeof data.tags === 'string') {
      tagsArray = (data.tags as string).split(',').map((tag: string) => tag.trim());
    }

    const beatType = data.beat_type === 'collaboration' ? 'collaboration' : 'cypher';
    const versesCount =
      beatType === 'cypher' ? data.verses_count || 10 : data.verses_count || 10;

    const { data: beat, error } = await supabase
      .from('beats')
      .insert([{
        title: data.title,
        genre: data.genre,
        bpm: data.bpm,
        file_url: data.file_url,
        is_public: data.is_public,
        producer_id: user.id,
        user_id: user.id,
        state: profile.state,
        tags: tagsArray,
        verses_count: versesCount,
        beat_type: beatType,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select(`
        *,
        producer:profiles!producer_id(*)
      `)
      .single();

    if (error) {
      console.error('Error creating beat:', error);
      throw error;
    }

    return beat;
  },

  async updateBeatVersesCount(beatId: string, versesCount: number): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify the user is the producer of this beat
    const { data: beat, error: fetchError } = await supabase
      .from('beats')
      .select('producer_id')
      .eq('id', beatId)
      .single();

    if (fetchError || !beat) {
      throw new Error('Beat not found');
    }

    if (beat.producer_id !== user.id) {
      throw new Error('Only the producer can update verses count');
    }

    const { error } = await supabase
      .from('beats')
      .update({ 
        verses_count: versesCount,
        updated_at: new Date().toISOString()
      })
      .eq('id', beatId);

    if (error) {
      console.error('Error updating verses count:', error);
      throw error;
    }
  },

  async createVerse(data: CreateVerseData): Promise<Verse> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Fetch the user's profile to get the username
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('username')
      .eq('id', user.id)
      .single();
    if (profileError || !profile) {
      throw new Error('Could not fetch user profile for username');
    }

    const { data: verse, error } = await supabase
      .from('verses')
      .insert([{
        ...data,
        rapper_id: user.id,
        user_id: user.id,
        rapper_username: profile.username, // denormalized username
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select(`
        *,
        rapper:profiles!rapper_id(*),
        beat:beats(*)
      `)
      .single();

    if (error) {
      console.error('Error creating verse:', error);
      throw error;
    }

    return verse;
  },

  async getBeats(filters: ContentFilters = {}): Promise<Beat[]> {
    // First, fetch the beats with producer info
    let query = supabase
      .from('beats')
      .select(`
        *,
        producer:profiles!producer_id(*)
      `);

    // Apply filters
    if (filters.userOnly && filters.userId) {
      query = query.eq('producer_id', filters.userId);
    } else {
      query = query.eq('is_public', true);
    }

    if (filters.genre) {
      query = query.eq('genre', filters.genre);
    }

    if (filters.state) {
      query = query.eq('state', filters.state);
    }

    if (filters.beatType === 'cypher' || filters.beatType === 'collaboration') {
      query = query.eq('beat_type', filters.beatType);
    }

    // Apply sorting
    if (filters.sort === 'popular') {
      query = query.order('vote_count', { ascending: false });
    } else {
      query = query.order('created_at', { ascending: false });
    }

    // Apply pagination
    if (typeof filters.offset === 'number' && typeof filters.limit === 'number') {
      query = query.range(filters.offset, filters.offset + filters.limit - 1);
    }

    const { data: beats, error } = await query;

    if (error) {
      console.error('Error fetching beats:', error);
      throw error;
    }

    if (!beats) return [];

    // Get the current user's ID
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;

    // Fetch votes for all beats in a single query
    const { data: votes, error: votesError } = await supabase
      .from('votes')
      .select('*')
      .in('content_id', beats.map(b => b.id))
      .eq('content_type', 'beat');

    if (votesError) {
      console.error('Error fetching votes:', votesError);
      throw votesError;
    }

    // Process and combine the data
    let processedBeats = beats.map(beat => {
      const beatVotes = votes?.filter(v => v.content_id === beat.id) || [];
      console.log('[getBeats] Votes for beat', beat.id, beatVotes);
      const voteCount = beatVotes.reduce((acc, v) => acc + (v.vote || 0), 0);
      const userVote = userId ? beatVotes.find(v => v.user_id === userId)?.vote : undefined;

      return {
        ...beat,
        vote_count: voteCount,
        user_vote: userVote
      };
    });

    return processedBeats;
  },

  async getVerses(filters: ContentFilters): Promise<Verse[]> {
    // First, fetch verses with basic info
    let query = supabase
      .from('verses')
      .select(`
        *,
        rapper:profiles!rapper_id(*),
        beat:beats(*, producer:profiles!producer_id(*))
      `);

    if (filters.state) {
      query = query.eq('rapper.state', filters.state);
    }
    if (filters.beatId) {
      query = query.eq('beat_id', filters.beatId);
    }
    // Submissions are private between the rapper and the beat's producer. RLS
    // enforces that; asking for the right rows here as well means a dropped
    // policy or an un-migrated environment can't quietly turn into a leak.
    if (filters.verseStatus === 'visible') {
      const { data: { session } } = await supabase.auth.getSession();
      const viewerId = session?.user?.id;
      query = viewerId
        ? query.or(`status.eq.approved,rapper_id.eq.${viewerId}`)
        : query.eq('status', 'approved');
    } else if (filters.verseStatus !== 'all') {
      query = query.eq('status', filters.verseStatus || 'approved');
    }
    // Apply sorting
    if (filters.sort === 'popular') {
      query = query.order('vote_count', { ascending: false });
    } else {
      query = query.order('created_at', { ascending: false });
    }

    // Apply pagination
    if (typeof filters.offset === 'number' && typeof filters.limit === 'number') {
      query = query.range(filters.offset, filters.offset + filters.limit - 1);
    }

    const { data: verses, error } = await query;

    if (error) {
      console.error('Error fetching verses:', error);
      throw error;
    }

    if (!verses) return [];

    // Feed queries only surface verses on public beats. On a single beat's page
    // that filter is redundant — RLS already decided whether the caller may see
    // the beat — and it would blank out the list for the owner of a private one.
    const publicVerses = filters.beatId
      ? verses.filter(v => !!v.beat)
      : verses.filter(v => v.beat?.is_public === true);

    // Get the current user's ID
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;

    const visibleVerses = publicVerses.filter(v => isVerseVisibleToViewer(v, userId));

    // Fetch votes for all verses in a single query
    const { data: votes, error: votesError } = await supabase
      .from('votes')
      .select('*')
      .in('content_id', visibleVerses.map(v => v.id))
      .eq('content_type', 'verse');

    if (votesError) {
      console.error('Error fetching votes:', votesError);
      throw votesError;
    }

    // Process and combine the data
    const processedVerses = visibleVerses.map(verse => {
      const verseVotes = votes?.filter(v => v.content_id === verse.id) || [];
      console.log('[getVerses] Votes for verse', verse.id, verseVotes);
      const voteCount = verseVotes.reduce((acc, v) => acc + (v.vote || 0), 0);
      const userVote = userId ? verseVotes.find(v => v.user_id === userId)?.vote : undefined;

      return {
        ...verse,
        vote_count: voteCount,
        user_vote: userVote
      };
    });

    return processedVerses;
  },

  async vote(contentType: 'beat' | 'verse', contentId: string, value: 1 | -1): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    const { error: upsertError } = await supabase
      .from('votes')
      .upsert({
        user_id: user.id, // Ensure user_id is set for unique constraint
        content_type: contentType,
        content_id: contentId,
        vote: value,
      }, {
        onConflict: 'user_id,content_type,content_id'
      });

    if (upsertError) {
      console.error('Error voting:', upsertError);
      throw upsertError;
    }
  },

  async deleteVote(contentType: 'beat' | 'verse', contentId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    const { error } = await supabase
      .from('votes')
      .delete()
      .match({
        user_id: user.id, // Only delete the current user's vote
        content_type: contentType,
        content_id: contentId,
      });

    if (error) {
      console.error('Error deleting vote:', error);
      throw error;
    }
  },

  async getBeatsCount(filters: ContentFilters = {}): Promise<{ count: number }> {
    let query = supabase
      .from('beats')
      .select('id', { count: 'exact', head: true });

    if (filters.userOnly && filters.userId) {
      query = query.eq('producer_id', filters.userId);
    } else {
      query = query.eq('is_public', true);
    }
    if (filters.genre) {
      query = query.eq('genre', filters.genre);
    }
    if (filters.state) {
      query = query.eq('state', filters.state);
    }
    if (filters.beatType === 'cypher' || filters.beatType === 'collaboration') {
      query = query.eq('beat_type', filters.beatType);
    }
    // No need for offset/limit for count
    const { count, error } = await query;
    if (error) throw error;
    return { count: count || 0 };
  },

  async getVersesCount(filters: ContentFilters = {}): Promise<{ count: number }> {
    let query = supabase
      .from('verses')
      .select(`
        *,
        beat:beats(*)
      `);
    if (filters.state) {
      query = query.eq('rapper.state', filters.state);
    }
    if (filters.verseStatus === 'visible') {
      const { data: { session } } = await supabase.auth.getSession();
      const viewerId = session?.user?.id;
      query = viewerId
        ? query.or(`status.eq.approved,rapper_id.eq.${viewerId}`)
        : query.eq('status', 'approved');
    } else if (filters.verseStatus !== 'all') {
      query = query.eq('status', filters.verseStatus || 'approved');
    }
    const { data: verses, error } = await query;
    if (error) throw error;
    if (!verses) return { count: 0 };
    // Filter out verses where beat.is_public !== true
    const publicVerses = verses.filter(v => v.beat?.is_public === true);
    const { data: { user } } = await supabase.auth.getUser();
    const visible = publicVerses.filter(v => isVerseVisibleToViewer(v, user?.id));
    return { count: visible.length };
  },

  /**
   * Verses submitted on the signed-in producer's beats, newest first.
   * The inner join keeps it to one round trip.
   */
  async getSubmissions(
    producerId: string,
    status: VerseStatus = 'pending'
  ): Promise<Verse[]> {
    const { data, error } = await supabase
      .from('verses')
      .select(`
        *,
        rapper:profiles!rapper_id(*),
        beat:beats!inner(*, producer:profiles!producer_id(*))
      `)
      .eq('beat.producer_id', producerId)
      .eq('status', status)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data as Verse[] | null) ?? [];
  },

  async getPendingSubmissionsCount(): Promise<number> {
    const { data, error } = await supabase.rpc('get_pending_verse_count');
    if (error) throw error;
    return Number(data ?? 0);
  },

  /** Producer approves or declines a verse on their collaboration beat. */
  async reviewVerse(verseId: string, status: 'approved' | 'declined' | 'pending'): Promise<void> {
    const { error } = await supabase.rpc('review_verse', {
      verse_id: verseId,
      new_status: status,
    });
    if (error) throw error;
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(VERSE_REVIEWED_EVENT));
    }
  },

  /**
   * Removes a verse row and its audio file. Storage cleanup is best effort —
   * an orphaned file is preferable to a row that won't go away.
   */
  async deleteVerse(verse: { id: string; file_url?: string | null }): Promise<void> {
    if (verse.file_url) {
      const filePath = storageService.getStoragePathFromUrl(verse.file_url, 'verses');
      if (filePath) {
        const { error: storageError } = await supabase.storage
          .from('verses')
          .remove([filePath]);
        if (storageError) {
          console.warn('Verse file removal failed (record will still be deleted):', storageError);
        }
      }
    }

    const { error } = await supabase.from('verses').delete().eq('id', verse.id);
    if (error) throw error;
  }
}; 